import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Container } from "reactstrap";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Link,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  CloudProviderContext,
  getAccountCloudType,
} from "../../../CloudProviderContext";
import {
  useAccounts,
  useOrganization,
  useUsers,
} from "../../../components/Common/HelperComponents/GetDataFromStore";
import CloudProviderMark from "../../../components/Common/CloudProviderMark";
import { getComplianceIcon } from "../../../assets/images/compliance/IconCompliance.jsx";
import {
  addAuditAccount,
  createAudit as createAuditApi,
  deleteAudit,
  listAllCatalogFrameworks,
  unwrapPost,
} from "../../../helpers/auditHub_helper";
import {
  AUDIT_NAME_MAX,
  AWS_ACCOUNT_NUMBER,
  CREATE_DRAFT_STORAGE_KEY,
  MAX_ACCOUNT_FRAMEWORKS,
  MAX_ACCOUNT_REGIONS,
  MAX_AUDIT_ACCOUNTS,
} from "./constants";
import DueDateField from "./DueDateField";
import LaunchAuditDialog from "./components/LaunchAuditDialog";
import { AWS_REGION_CODES, auditorCandidates, excludeMspUsers, formatUserLabel, formatUserName, frameworkLabel } from "./utils";
import useMspUsers from "./useMspUsers";
import {
  createAudit,
  fetchAudits,
  resetCreateAudit,
} from "../../../store/auditHub/auditHubSlice";
import {
  AH_ACCENT,
  AH_ACCENT_HOVER,
  AH_BORDER,
  AH_INPUT_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_TRACK,
  AH_WHITE,
  AuditHubThemeProvider,
  auditHubPageSx,
  backButtonSx,
  cardShellSx,
  outlinedButtonSx,
  primaryButtonSx,
  searchFieldSx,
  tableShellSx,
} from "./theme";

const asInteger = (value) => {
  if (typeof value === "number" && Number.isInteger(value)) return value;
  if (typeof value === "string" && /^-?\d+$/.test(value.trim())) return Number(value.trim());
  return null;
};

const regionChipSx = (selected) => ({
  height: 28,
  borderRadius: "8px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
  color: AH_TEXT,
  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
});

const regionsSummary = (regions) => {
  if (!regions.length) return "All regions";
  return regions.length === 1 ? regions[0] : `${regions.length} regions`;
};

const emptyAccount = (accountNumber, regions = []) => ({
  accountNumber,
  regions,
  complianceAddonIds: [],
});

const normalizeRegions = (value) => {
  if (!Array.isArray(value)) return [];
  const allowed = new Set(AWS_REGION_CODES);
  return [...new Set(value.filter((region) => allowed.has(region)))].slice(0, MAX_ACCOUNT_REGIONS);
};

const emptyForm = () => ({
  name: "",
  description: "",
  ownerEmail: "",
  defaultAssigneeEmail: "",
  auditorEmails: [],
  dueDate: "",
  frameworkIds: [],
  accounts: [],
});

/** One framework per audit; kept as a list because that is what accounts are sent with. */
const normalizeFrameworkIds = (value) =>
  Array.isArray(value) ? [...new Set(value.map(asInteger).filter((id) => id != null))].slice(0, 1) : [];

const normalizeAccounts = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .filter((entry) => entry && typeof entry.accountNumber === "string")
    .slice(0, MAX_AUDIT_ACCOUNTS)
    .map((entry) => ({
      accountNumber: entry.accountNumber,
      regions: normalizeRegions(entry.regions),
      complianceAddonIds: Array.isArray(entry.complianceAddonIds)
        ? entry.complianceAddonIds.map(asInteger).filter((id) => id != null).slice(0, MAX_ACCOUNT_FRAMEWORKS)
        : [],
    }));
};

const readDraft = () => {
  try {
    const raw = sessionStorage.getItem(CREATE_DRAFT_STORAGE_KEY);
    if (!raw) return emptyForm();
    const parsed = JSON.parse(raw);
    // Drafts saved before regions moved onto each account carry one audit-wide list.
    const legacyRegions = normalizeRegions(parsed.regions);
    const accounts = normalizeAccounts(parsed.accounts).map((entry) =>
      entry.regions.length || !legacyRegions.length ? entry : { ...entry, regions: legacyRegions }
    );
    if (!accounts.length && parsed.accountNumber) {
      const addonId = asInteger(parsed.complianceAddonId);
      accounts.push({
        ...emptyAccount(parsed.accountNumber, legacyRegions),
        complianceAddonIds: addonId == null ? [] : [addonId],
      });
    }
    return {
      ...emptyForm(),
      name: typeof parsed.name === "string" ? parsed.name : "",
      description: typeof parsed.description === "string" ? parsed.description : "",
      ownerEmail: typeof parsed.ownerEmail === "string" ? parsed.ownerEmail : "",
      defaultAssigneeEmail: typeof parsed.defaultAssigneeEmail === "string" ? parsed.defaultAssigneeEmail : "",
      auditorEmails: Array.isArray(parsed.auditorEmails)
        ? parsed.auditorEmails.filter((email) => typeof email === "string" && email)
        : [],
      dueDate: typeof parsed.dueDate === "string" ? parsed.dueDate : "",
      // Drafts saved before frameworks came first only carry per-account ids.
      frameworkIds: normalizeFrameworkIds(
        Array.isArray(parsed.frameworkIds) ? parsed.frameworkIds : accounts.flatMap((entry) => entry.complianceAddonIds)
      ),
      accounts,
    };
  } catch (_e) {
    return emptyForm();
  }
};

const writeDraft = (form) => {
  try {
    sessionStorage.setItem(CREATE_DRAFT_STORAGE_KEY, JSON.stringify(form));
  } catch (_e) {
    /* ignore */
  }
};

const clearDraft = () => {
  try {
    sessionStorage.removeItem(CREATE_DRAFT_STORAGE_KEY);
  } catch (_e) {
    /* ignore */
  }
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    backgroundColor: AH_WHITE,
    "& fieldset": { borderColor: AH_INPUT_BORDER },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: AH_TEXT, borderWidth: "1px" },
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
};

const fieldLabelSx = {
  display: "block",
  fontSize: 14,
  fontWeight: 600,
  color: AH_TEXT,
  lineHeight: 1.3,
  mb: 0.75,
};

const fieldHintSx = {
  fontSize: 12,
  color: AH_MUTED,
  lineHeight: 1.45,
  mb: 0.75,
};

const auditorChipSx = {
  height: 24,
  borderRadius: "999px",
  backgroundColor: "#e8f1ff",
  color: "#3d6cb8",
  fontSize: 12,
  fontWeight: 500,
  "& .MuiChip-label": { px: 1 },
  "& .MuiChip-deleteIcon": {
    color: "#3d6cb8",
    fontSize: 16,
    "&:hover": { color: "#2c528f" },
  },
};

const FieldLabel = ({ children, required, htmlFor }) => (
  <Typography component="label" htmlFor={htmlFor} sx={fieldLabelSx}>
    {children}
    {required ? (
      <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
        *
      </Box>
    ) : null}
  </Typography>
);

const clampSx = (lines) => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
});

const SectionCard = ({ title, subtitle, action, children, sx, required }) => (
  <Box sx={{ ...tableShellSx, ...sx }}>
    {(title || action) && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
          mb: children ? 2 : 0,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          {title ? (
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: AH_TEXT }}>
              {title}
              {required ? (
                <Box component="span" sx={{ color: "#dc2626", ml: 0.4 }} aria-hidden="true">
                  *
                </Box>
              ) : null}
            </Typography>
          ) : null}
          {subtitle ? (
            <Typography sx={{ fontSize: 13, color: AH_MUTED, mt: 0.25 }}>{subtitle}</Typography>
          ) : null}
        </Box>
        {action}
      </Box>
    )}
    {children}
  </Box>
);

const FRAMEWORK_CARD_MIN_HEIGHT = 96;

const frameworkGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: 1.25,
  alignItems: "stretch",
};

const frameworkListSx = {
  maxHeight: (theme) => `calc(${FRAMEWORK_CARD_MIN_HEIGHT}px * 2 + ${theme.spacing(1.25)})`,
  overflowY: "auto",
  pr: 0.5,
};

const manualControlsText = (fw) =>
  Number.isInteger(fw.manualControlCount)
    ? `${fw.manualControlCount} manual control${fw.manualControlCount === 1 ? "" : "s"}`
    : "—";

const CATALOG_CONCURRENCY = 4;

const joinWithAnd = (items) =>
  items.length <= 1 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

const FrameworkCards = ({ frameworks, selectedIds, onSelect, detail = manualControlsText }) => (
  <Box sx={frameworkGridSx}>
    {frameworks.map((fw) => {
      const addonId = asInteger(fw.complianceAddonId);
      const selected = addonId != null && selectedIds.includes(addonId);
      const label = frameworkLabel(fw);
      const icon = getComplianceIcon(label);
      return (
        <Box
          key={fw.complianceAddonId}
          component="button"
          type="button"
          disabled={addonId == null}
          aria-pressed={selected}
          onClick={() => onSelect(addonId)}
          sx={{
            ...cardShellSx,
            minHeight: FRAMEWORK_CARD_MIN_HEIGHT,
            p: 1.5,
            textAlign: "left",
            cursor: "pointer",
            border: `1px solid ${selected ? AH_ACCENT : AH_BORDER}`,
            backgroundColor: selected ? "#fff4eb" : AH_WHITE,
            display: "flex",
            alignItems: "flex-start",
            gap: 1.25,
            fontFamily: "inherit",
            "&:hover": { borderColor: selected ? AH_ACCENT : "#cbd5e1" },
          }}
        >
          <Box sx={{ width: 28, height: 28, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            {icon ? (
              <Box component="img" src={icon} alt="" sx={{ maxHeight: 24, maxWidth: 28 }} />
            ) : (
              <CheckCircleOutlineIcon sx={{ fontSize: 20, color: AH_MUTED }} />
            )}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Tooltip title={label} placement="top">
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: AH_TEXT, lineHeight: 1.35, ...clampSx(2) }}>
                {label}
              </Typography>
            </Tooltip>
            <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 0.5, lineHeight: 1.4, ...clampSx(2) }}>
              {detail(fw)}
            </Typography>
          </Box>
          {selected ? <CheckCircleOutlineIcon sx={{ fontSize: 18, color: AH_ACCENT, flexShrink: 0 }} /> : null}
        </Box>
      );
    })}
  </Box>
);

const CreateAudit = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const accounts = useAccounts();
  const organization = useOrganization();
  const { users, usersLoading } = useUsers();
  const mspUsers = useMspUsers();
  const createState = useSelector((s) => s.auditHub?.create) || {};
  const [form, setForm] = useState(readDraft);
  const [errors, setErrors] = useState({});
  const [accountQuery, setAccountQuery] = useState("");
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [frameworkQuery, setFrameworkQuery] = useState("");
  const [catalogByAccount, setCatalogByAccount] = useState({});
  const [catalogRetry, setCatalogRetry] = useState(0);
  const [formError, setFormError] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  const skipFirstSave = useRef(true);
  const catalogRef = useRef({});
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    document.title = "Create New Assessment";
    dispatch(resetCreateAudit());
  }, [dispatch]);

  useEffect(() => {
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return undefined;
    }
    const handle = setTimeout(() => {
      writeDraft(form);
    }, 700);
    return () => clearTimeout(handle);
  }, [form]);

  useEffect(() => {
    setFormError("");
  }, [form]);

  useEffect(() => {
    if (createState.status === "succeeded") {
      const failures = Array.isArray(createState.audit?.scanDispatchFailures)
        ? createState.audit.scanDispatchFailures.filter(Boolean)
        : [];
      clearDraft();
      dispatch(fetchAudits({ page: 1, size: 100 }));
      dispatch(resetCreateAudit());
      history.push({
        pathname: "/audit-hub",
        state: failures.length ? { scanDispatchFailures: failures } : undefined,
      });
    } else if (createState.status === "failed" && createState.error) {
      setFormError(createState.error);
    }
  }, [createState.status, createState.error, dispatch, history]);

  const userOptions = useMemo(
    () =>
      excludeMspUsers(users, organization)
        .filter((user) => user.email)
        .map((user) => ({
          email: user.email,
          name: formatUserName(user) || user.email,
          label: formatUserLabel(user),
        })),
    [users, organization]
  );
  const auditorOptions = useMemo(
    () =>
      auditorCandidates({ users, organization, msp: mspUsers })
        .filter((user) => user.email)
        .map((user) => ({
          email: user.email,
          name: formatUserName(user) || user.email,
          label: formatUserLabel(user),
        })),
    [users, organization, mspUsers]
  );
  const auditorsLoading = usersLoading || mspUsers.loading;

  const providerAccounts = useMemo(() => {
    if (!Array.isArray(accounts)) return [];
    return accounts.filter((account) => {
      if (account.accountNumber?.includes("INIT00000001")) return false;
      if (account.accountNumber?.includes("TEST")) return false;
      return getAccountCloudType(account) === selectedProvider;
    });
  }, [accounts, selectedProvider]);

  const providerAccountKey = providerAccounts
    .map((account) => account.accountNumber)
    .filter((accountNumber) => AWS_ACCOUNT_NUMBER.test(accountNumber || ""))
    .join(",");

  useEffect(() => {
    if (usersLoading || !Array.isArray(users)) return undefined;
    const emails = new Set(userOptions.map((option) => option.email));
    const auditorSet = new Set(auditorOptions.map((option) => option.email));
    // Until the MSP users are known, a restored auditor cannot be judged invalid.
    const auditorsKnown = !mspUsers.loading && !mspUsers.error;
    setForm((prev) => {
      const ownerEmail = emails.has(prev.ownerEmail) ? prev.ownerEmail : "";
      const defaultAssigneeEmail = emails.has(prev.defaultAssigneeEmail) ? prev.defaultAssigneeEmail : "";
      const auditorEmails = auditorsKnown
        ? (prev.auditorEmails || []).filter((email) => auditorSet.has(email))
        : prev.auditorEmails || [];
      const auditorsUnchanged =
        auditorEmails.length === (prev.auditorEmails || []).length &&
        auditorEmails.every((email, index) => email === prev.auditorEmails[index]);
      if (
        ownerEmail === prev.ownerEmail &&
        defaultAssigneeEmail === prev.defaultAssigneeEmail &&
        auditorsUnchanged
      ) {
        return prev;
      }
      return { ...prev, ownerEmail, defaultAssigneeEmail, auditorEmails };
    });
    return undefined;
  }, [userOptions, auditorOptions, users, usersLoading, mspUsers.loading, mspUsers.error]);

  // There is no framework -> accounts route, so each account's own catalog (the eligibility filter
  // POST /audits/{id}/accounts enforces) is read once, a few at a time, and inverted here.
  useEffect(() => {
    const pending = providerAccountKey
      .split(",")
      .filter((accountNumber) => accountNumber && !catalogRef.current[accountNumber]);
    if (!pending.length) return;
    pending.forEach((accountNumber) => {
      catalogRef.current[accountNumber] = { status: "loading", items: [], error: null };
    });
    setCatalogByAccount({ ...catalogRef.current });
    let index = 0;
    const loadNext = async () => {
      while (index < pending.length) {
        const accountNumber = pending[index];
        index += 1;
        try {
          const items = await listAllCatalogFrameworks({ accountNumber });
          catalogRef.current[accountNumber] = { status: "succeeded", items, error: null };
        } catch (error) {
          catalogRef.current[accountNumber] = {
            status: "failed",
            items: [],
            error: error?.message || "Unable to load frameworks.",
          };
        }
        if (mountedRef.current) setCatalogByAccount({ ...catalogRef.current });
      }
    };
    for (let i = 0; i < Math.min(CATALOG_CONCURRENCY, pending.length); i += 1) loadNext();
  }, [providerAccountKey, catalogRetry]);

  const retryFailedCatalogs = () => {
    Object.keys(catalogRef.current).forEach((accountNumber) => {
      if (catalogRef.current[accountNumber]?.status === "failed") delete catalogRef.current[accountNumber];
    });
    setCatalogRetry((n) => n + 1);
  };

  const eligibleIdsByAccount = useMemo(() => {
    const map = {};
    Object.entries(catalogByAccount).forEach(([accountNumber, catalog]) => {
      if (catalog?.status !== "succeeded") return;
      map[accountNumber] = new Set(
        (catalog.items || []).map((fw) => asInteger(fw.complianceAddonId)).filter((id) => id != null)
      );
    });
    return map;
  }, [catalogByAccount]);

  const catalogProgress = useMemo(() => {
    const numbers = providerAccountKey.split(",").filter(Boolean);
    const statuses = numbers.map((accountNumber) => catalogByAccount[accountNumber]?.status);
    return {
      total: numbers.length,
      loading: statuses.filter((status) => status === "loading" || !status).length,
      failed: statuses.filter((status) => status === "failed").length,
    };
  }, [providerAccountKey, catalogByAccount]);

  /** Frameworks at least one of this provider's accounts can be audited on, with how many can. */
  const frameworkOptions = useMemo(() => {
    const byId = new Map();
    providerAccountKey.split(",").filter(Boolean).forEach((accountNumber) => {
      const catalog = catalogByAccount[accountNumber];
      if (catalog?.status !== "succeeded") return;
      (catalog.items || []).forEach((fw) => {
        const id = asInteger(fw.complianceAddonId);
        if (id == null) return;
        const entry = byId.get(id) || { ...fw, complianceAddonId: id, eligibleAccounts: 0 };
        entry.eligibleAccounts += 1;
        byId.set(id, entry);
      });
    });
    return [...byId.values()].sort((a, b) => frameworkLabel(a).localeCompare(frameworkLabel(b)));
  }, [providerAccountKey, catalogByAccount]);

  const visibleFrameworks = useMemo(() => {
    const q = frameworkQuery.trim().toLowerCase();
    if (!q) return frameworkOptions;
    return frameworkOptions.filter((fw) => frameworkLabel(fw).toLowerCase().includes(q));
  }, [frameworkOptions, frameworkQuery]);

  const scopeIdsFor = (accountNumber) => {
    const eligible = eligibleIdsByAccount[accountNumber];
    return eligible ? form.frameworkIds.filter((id) => eligible.has(id)) : [];
  };

  /** Accounts that can be audited on at least one selected framework. */
  const candidateAccounts = useMemo(() => {
    if (!form.frameworkIds.length) return [];
    return providerAccounts.filter((account) => {
      const eligible = eligibleIdsByAccount[account.accountNumber];
      return Boolean(eligible) && form.frameworkIds.some((id) => eligible.has(id));
    });
  }, [providerAccounts, eligibleIdsByAccount, form.frameworkIds]);

  const visibleAccounts = useMemo(() => {
    const q = accountQuery.trim().toLowerCase();
    if (!q) return candidateAccounts;
    return candidateAccounts.filter((account) =>
      `${account.accountName || ""} ${account.accountNumber || ""}`.toLowerCase().includes(q)
    );
  }, [candidateAccounts, accountQuery]);

  useEffect(() => {
    if (!providerAccounts.length) return;
    const allowed = new Set(providerAccounts.map((account) => account.accountNumber));
    setForm((prev) => {
      const accountsNext = prev.accounts.filter((entry) => allowed.has(entry.accountNumber));
      if (accountsNext.length === prev.accounts.length) return prev;
      return { ...prev, accounts: accountsNext };
    });
  }, [providerAccounts]);

  // Once every catalog has answered, a restored framework no account offers is dropped.
  useEffect(() => {
    if (catalogProgress.loading || catalogProgress.failed) return;
    const offered = new Set(frameworkOptions.map((fw) => fw.complianceAddonId));
    setForm((prev) => {
      const frameworkIds = prev.frameworkIds.filter((id) => offered.has(id));
      return frameworkIds.length === prev.frameworkIds.length ? prev : { ...prev, frameworkIds };
    });
  }, [frameworkOptions, catalogProgress.loading, catalogProgress.failed]);

  // An account stays selected only while it can be audited on a selected framework.
  useEffect(() => {
    setForm((prev) => {
      const accountsNext = prev.accounts.filter((entry) => {
        const eligible = eligibleIdsByAccount[entry.accountNumber];
        if (!eligible) return catalogByAccount[entry.accountNumber]?.status !== "failed";
        return prev.frameworkIds.some((id) => eligible.has(id));
      });
      return accountsNext.length === prev.accounts.length ? prev : { ...prev, accounts: accountsNext };
    });
  }, [form.frameworkIds, eligibleIdsByAccount, catalogByAccount]);

  const patch = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const toggleAccount = (account) => {
    const wasSelected = form.accounts.some((entry) => entry.accountNumber === account.accountNumber);
    setExpandedAccount((current) => {
      if (!wasSelected) return account.accountNumber;
      return current === account.accountNumber ? null : current;
    });
    setForm((prev) => {
      const exists = prev.accounts.some((entry) => entry.accountNumber === account.accountNumber);
      if (exists) {
        return {
          ...prev,
          accounts: prev.accounts.filter((entry) => entry.accountNumber !== account.accountNumber),
        };
      }
      if (prev.accounts.length >= MAX_AUDIT_ACCOUNTS) return prev;
      return { ...prev, accounts: [...prev.accounts, emptyAccount(account.accountNumber)] };
    });
    setErrors((prev) => ({ ...prev, accounts: undefined, scopes: undefined }));
  };

  /** `code` null means all regions. */
  const toggleAccountRegion = (accountNumber, code) => {
    setForm((prev) => ({
      ...prev,
      accounts: prev.accounts.map((entry) => {
        if (entry.accountNumber !== accountNumber) return entry;
        if (code == null) return entry.regions.length ? { ...entry, regions: [] } : entry;
        if (entry.regions.includes(code)) return { ...entry, regions: entry.regions.filter((region) => region !== code) };
        if (entry.regions.length >= MAX_ACCOUNT_REGIONS) return entry;
        return { ...entry, regions: [...entry.regions, code] };
      }),
    }));
  };

  const selectFramework = (addonId) => {
    if (addonId == null) return;
    setForm((prev) => ({ ...prev, frameworkIds: prev.frameworkIds[0] === addonId ? [] : [addonId] }));
    setErrors((prev) => ({ ...prev, scopes: undefined, accounts: undefined }));
  };

  const missing = [];
  if (!form.name.trim()) missing.push("an assessment name");
  if (!form.ownerEmail) missing.push("an assessment owner");
  if (!form.frameworkIds.length) missing.push("a framework");
  if (!form.dueDate) missing.push("a due date");
  if (!form.accounts.length) missing.push("at least one account");
  const scopesReady = form.accounts.every((entry) => scopeIdsFor(entry.accountNumber).length > 0);
  const nameTooLong = form.name.trim().length > AUDIT_NAME_MAX;
  const ready = missing.length === 0 && scopesReady && !nameTooLong;
  let footerMessage = formError;
  if (!footerMessage && !ready) {
    footerMessage = missing.length
      ? `Add ${joinWithAnd(missing)} to continue.`
      : nameTooLong
      ? `Shorten the assessment name to ${AUDIT_NAME_MAX} characters or fewer.`
      : "Each selected account must support the selected framework.";
  }

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Assessment name is required.";
    else if (nameTooLong) next.name = `Assessment name can be at most ${AUDIT_NAME_MAX} characters.`;
    if (!form.ownerEmail) next.ownerEmail = "Assessment owner is required.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.dueDate || "")) next.dueDate = "Due date is required.";
    if (!form.frameworkIds.length) next.scopes = "Select a framework.";
    if (!form.accounts.length) {
      next.accounts = "Select at least one account.";
    } else if (form.accounts.length > MAX_AUDIT_ACCOUNTS) {
      next.accounts = `An assessment can include at most ${MAX_AUDIT_ACCOUNTS} accounts.`;
    } else if (!scopesReady) {
      next.accounts = "Each selected account must support the selected framework.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildAccounts = () =>
    form.accounts.map((entry) => {
      const account = { accountNumber: entry.accountNumber, complianceAddonIds: scopeIdsFor(entry.accountNumber) };
      if (entry.regions.length) account.regions = entry.regions;
      return account;
    });

  const handleSaveDraft = async () => {
    if (savingDraft) return;
    if (!validate()) {
      setFormError("Complete the required fields before saving the draft.");
      return;
    }
    setFormError("");
    const shell = { name: form.name.trim(), ownerEmail: form.ownerEmail, dueDate: form.dueDate };
    if (form.defaultAssigneeEmail) shell.defaultAssigneeEmail = form.defaultAssigneeEmail;
    if ((form.auditorEmails || []).length) shell.auditorEmails = form.auditorEmails;

    setSavingDraft(true);
    let auditId = null;
    try {
      const created = unwrapPost(await createAuditApi(shell));
      auditId = created?.auditId;
      if (!auditId) throw new Error("The draft was created without an id.");
      for (const account of buildAccounts()) {
        unwrapPost(await addAuditAccount(auditId, account));
      }
      clearDraft();
      dispatch(fetchAudits({ page: 1, size: 100 }));
      history.push({
        pathname: "/audit-hub",
        state: { draftSaved: created?.auditCode || created?.name || true },
      });
    } catch (error) {
      if (auditId) {
        try {
          await deleteAudit(auditId);
        } catch (_cleanupError) {
          /* the original error is the one to show */
        }
      }
      setSavingDraft(false);
      setFormError(error?.message || "Unable to save the draft.");
    }
  };

  const handleLaunch = () => {
    if (!validate()) {
      setFormError("Complete the required fields before launching the assessment.");
      return;
    }
    setFormError("");
    if (createState.status === "failed") dispatch(resetCreateAudit());
    setConfirmLaunch(true);
  };

  const submitLaunch = () => {
    if (!validate()) {
      setConfirmLaunch(false);
      setFormError("Complete the required fields before launching the assessment.");
      return;
    }
    const body = {
      name: form.name.trim(),
      ownerEmail: form.ownerEmail,
      dueDate: form.dueDate,
      accounts: buildAccounts(),
    };
    if (form.defaultAssigneeEmail) body.defaultAssigneeEmail = form.defaultAssigneeEmail;
    if ((form.auditorEmails || []).length) body.auditorEmails = form.auditorEmails;
    dispatch(createAudit(body));
  };

  const ownerValue = userOptions.find((u) => u.email === form.ownerEmail) || null;
  const assigneeValue = userOptions.find((u) => u.email === form.defaultAssigneeEmail) || null;
  const auditorValues = auditorOptions.filter((option) => (form.auditorEmails || []).includes(option.email));
  const launching = createState.status === "loading";
  const frameworkCountText = (fw) => {
    const count = fw.eligibleAccounts || 0;
    return `${manualControlsText(fw)} · ${count} account${count === 1 ? "" : "s"}`;
  };

  return (
    <AuditHubThemeProvider>
      <div className="page-content" style={{ backgroundColor: "#fff" }}>
        <Container fluid>
          <Box
            sx={{
              pt: 1.5,
              pb: 4,
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
              ...auditHubPageSx,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
              <Breadcrumbs sx={{ fontSize: 13, color: AH_MUTED }}>
                <Link
                  component="button"
                  underline="hover"
                  color="inherit"
                  onClick={() => history.push("/audit-hub")}
                  sx={{ fontSize: 13 }}
                >
                  Assessments
                </Link>
                <Typography sx={{ fontSize: 13, color: AH_TEXT }}>Create New Assessment</Typography>
              </Breadcrumbs>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => history.push("/audit-hub")}
                sx={backButtonSx}
              >
                Back to Audit Hub
              </Button>
            </Box>

            <Box sx={tableShellSx}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", md: "1.25rem" },
                  color: AH_TEXT,
                  letterSpacing: "-0.01em",
                }}
              >
                Create New Assessment
              </Typography>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 5fr) minmax(0, 7fr)" },
                gridTemplateAreas: {
                  xs: `"meta" "timeline" "scope"`,
                  lg: `"meta scope" "timeline scope"`,
                },
                gap: 2,
                alignItems: "start",
              }}
            >
                <SectionCard
                  title="Metadata & Governance"
                  subtitle="Basic information, ownership, and responsible parties."
                  sx={{ gridArea: "meta", alignSelf: "stretch", minWidth: 0 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                      <FieldLabel required htmlFor="audit-name">Assessment Name</FieldLabel>
                      <TextField
                        id="audit-name"
                        required
                        fullWidth
                        size="small"
                        value={form.name}
                        onChange={(e) => patch({ name: e.target.value })}
                        error={Boolean(errors.name) || nameTooLong}
                        helperText={errors.name}
                        placeholder="Enter Assessment Name"
                        inputProps={{ maxLength: AUDIT_NAME_MAX, "aria-describedby": "audit-name-count" }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Typography
                                id="audit-name-count"
                                sx={{ fontSize: 12, color: nameTooLong ? "#dc2626" : AH_MUTED, fontVariantNumeric: "tabular-nums" }}
                              >
                                {form.name.length}/{AUDIT_NAME_MAX}
                              </Typography>
                            </InputAdornment>
                          ),
                        }}
                        sx={fieldSx}
                      />
                    </Box>
                    <Box>
                      <FieldLabel htmlFor="audit-description">Description</FieldLabel>
                      <TextField
                        id="audit-description"
                        fullWidth
                        size="small"
                        multiline
                        minRows={3}
                        value={form.description}
                        onChange={(e) => patch({ description: e.target.value })}
                        placeholder="Summarize what this Assessment covers and its purpose."
                        sx={fieldSx}
                      />
                    </Box>
                    <Box>
                      <FieldLabel required htmlFor="audit-owner">Assessment Owner</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={userOptions}
                        loading={usersLoading}
                        value={ownerValue}
                        onChange={(_e, option) => patch({ ownerEmail: option?.email || "" })}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(a, b) => a.email === b.email}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            id="audit-owner"
                            required
                            placeholder="Select Owner"
                            error={Boolean(errors.ownerEmail)}
                            helperText={
                              errors.ownerEmail ||
                              (!usersLoading && userOptions.length === 0
                                ? "No client-organization users are available. MSP users are not listed."
                                : undefined)
                            }
                            sx={fieldSx}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <FieldLabel htmlFor="audit-assignee">Default Assignee</FieldLabel>
                      <Autocomplete
                        size="small"
                        options={userOptions}
                        loading={usersLoading}
                        value={assigneeValue}
                        onChange={(_e, option) => patch({ defaultAssigneeEmail: option?.email || "" })}
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(a, b) => a.email === b.email}
                        renderInput={(params) => (
                          <TextField {...params} id="audit-assignee" placeholder="Select Assignee" sx={fieldSx} />
                        )}
                      />
                    </Box>
                    <Box>
                      <FieldLabel htmlFor="audit-reviewers">Auditors / Reviewers</FieldLabel>
                      <Typography sx={fieldHintSx}>
                        MSP users who review and decide controls on this assessment.
                      </Typography>
                      <Autocomplete
                        multiple
                        size="small"
                        options={auditorOptions}
                        loading={auditorsLoading}
                        loadingText="Loading MSP users…"
                        noOptionsText={mspUsers.error || "No MSP users are available."}
                        value={auditorValues}
                        onChange={(_e, options) =>
                          patch({ auditorEmails: options.map((option) => option.email) })
                        }
                        getOptionLabel={(option) => option.label || ""}
                        isOptionEqualToValue={(a, b) => a.email === b.email}
                        filterSelectedOptions
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const { key, ...tagProps } = getTagProps({ index });
                            return (
                              <Chip
                                key={key}
                                {...tagProps}
                                label={option.name || option.label}
                                size="small"
                                sx={auditorChipSx}
                              />
                            );
                          })
                        }
                        renderInput={(params) => (
                          <TextField {...params} id="audit-reviewers" placeholder="Select Auditors" sx={fieldSx} />
                        )}
                      />
                    </Box>
                  </Box>
                </SectionCard>

                <SectionCard
                  required
                  title="Timeline"
                  subtitle="Select the target completion date for this assessment. The start time is set when the assessment is launched."
                  sx={{ gridArea: "timeline", alignSelf: "stretch", minWidth: 0 }}
                >
                  <DueDateField
                    id="audit-due"
                    required
                    error={Boolean(errors.dueDate)}
                    helperText={errors.dueDate || ""}
                    value={form.dueDate}
                    onChange={(dueDate) => {
                      patch({ dueDate });
                      if (errors.dueDate) setErrors((prev) => ({ ...prev, dueDate: undefined }));
                    }}
                  />
                </SectionCard>
                <Box
                  sx={{
                    gridArea: "scope",
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    alignSelf: "stretch",
                  }}
                >
                  <SectionCard
                    required
                    title="Framework"
                    subtitle="Choose the framework for this assessment. Only frameworks your accounts support are listed."
                  >
                    {frameworkOptions.length ? (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search frameworks..."
                        value={frameworkQuery}
                        onChange={(e) => setFrameworkQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ ...searchFieldSx, mb: 1.25 }}
                      />
                    ) : null}
                    <Box sx={frameworkListSx}>
                      {frameworkOptions.length ? (
                        visibleFrameworks.length ? (
                          <FrameworkCards
                            frameworks={visibleFrameworks}
                            selectedIds={form.frameworkIds}
                            onSelect={selectFramework}
                            detail={frameworkCountText}
                          />
                        ) : (
                          <Typography sx={{ fontSize: 13, color: AH_MUTED }}>No frameworks match this search.</Typography>
                        )
                      ) : catalogProgress.loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 2 }}>
                          <CircularProgress size={20} />
                          <Typography sx={{ fontSize: 13, color: AH_MUTED }}>Loading frameworks for your accounts…</Typography>
                        </Box>
                      ) : catalogProgress.failed ? (
                        <Typography sx={{ fontSize: 13, color: "#dc2626" }}>Unable to load frameworks for your accounts.</Typography>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: AH_MUTED }}>
                          {catalogProgress.total
                            ? "No frameworks are available for your accounts."
                            : `No eligible ${selectedProvider === "AZURE" ? "subscriptions" : "accounts"} are available.`}
                        </Typography>
                      )}
                    </Box>
                    {frameworkOptions.length && catalogProgress.loading ? (
                      <Typography sx={{ fontSize: 12, color: AH_MUTED, mt: 1 }}>
                        Checking {catalogProgress.loading} more account{catalogProgress.loading === 1 ? "" : "s"}…
                      </Typography>
                    ) : null}
                    {!catalogProgress.loading && catalogProgress.failed ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 12, color: "#dc2626" }}>
                          Frameworks could not be checked for {catalogProgress.failed} account
                          {catalogProgress.failed === 1 ? "" : "s"}.
                        </Typography>
                        <Link component="button" type="button" underline="hover" onClick={retryFailedCatalogs} sx={{ fontSize: 12, color: AH_ACCENT }}>
                          Retry
                        </Link>
                      </Box>
                    ) : null}
                    {errors.scopes ? (
                      <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.scopes}</Typography>
                    ) : null}
                  </SectionCard>
                  <SectionCard
                    required
                    title={selectedProvider === "AZURE" ? "Azure Subscriptions" : "Cloud Accounts"}
                    subtitle="Select the accounts to include and the regions to scan on each account. Only accounts that support the selected framework are listed."
                    sx={{ flex: 1 }}
                    action={
                      <Typography sx={{ fontSize: 12, color: errors.accounts ? "#dc2626" : AH_MUTED, whiteSpace: "nowrap", pt: 0.25 }}>
                        {form.accounts.length} of {MAX_AUDIT_ACCOUNTS}
                      </Typography>
                    }
                  >
                    {candidateAccounts.length ? (
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={selectedProvider === "AZURE" ? "Search subscriptions..." : "Search accounts..."}
                        value={accountQuery}
                        onChange={(e) => setAccountQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ ...searchFieldSx, mb: 1.25 }}
                      />
                    ) : null}
                    <Box
                      sx={{
                        ...cardShellSx,
                        maxHeight: 360,
                        overflowY: "auto",
                        borderColor: errors.accounts ? "#dc2626" : AH_BORDER,
                      }}
                    >
                      {!form.frameworkIds.length ? (
                        <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED }}>
                          Select a framework first.
                        </Typography>
                      ) : !candidateAccounts.length ? (
                        <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED }}>
                          {catalogProgress.loading
                            ? "Checking which accounts support this framework…"
                            : `No ${selectedProvider === "AZURE" ? "subscriptions" : "accounts"} support the selected framework.`}
                        </Typography>
                      ) : visibleAccounts.length === 0 ? (
                        <Typography sx={{ p: 2, fontSize: 13, color: AH_MUTED }}>
                          No {selectedProvider === "AZURE" ? "subscriptions" : "accounts"} match this search.
                        </Typography>
                      ) : (
                        visibleAccounts.map((account) => {
                          const entry = form.accounts.find((item) => item.accountNumber === account.accountNumber);
                          const selected = Boolean(entry);
                          const unavailable = !selected && form.accounts.length >= MAX_AUDIT_ACCOUNTS;
                          const accountLabel = account.accountName || account.accountCode || account.accountNumber;
                          const expanded = selected && expandedAccount === account.accountNumber;
                          const panelId = `create-audit-regions-${account.accountNumber}`;
                          return (
                            <Box
                              key={account.accountCode || account.accountNumber}
                              sx={{
                                borderBottom: `1px solid ${AH_BORDER}`,
                                opacity: unavailable ? 0.45 : 1,
                                "&:last-of-type": { borderBottom: 0 },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  px: 1.5,
                                  backgroundColor: selected ? "#fff4eb" : AH_WHITE,
                                  "&:hover": { backgroundColor: selected ? "#fff4eb" : AH_TRACK },
                                }}
                              >
                                <Box
                                  role="checkbox"
                                  aria-checked={selected}
                                  aria-disabled={unavailable}
                                  tabIndex={unavailable ? -1 : 0}
                                  onClick={() => {
                                    if (!unavailable) toggleAccount(account);
                                  }}
                                  onKeyDown={(event) => {
                                    if (unavailable) return;
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      toggleAccount(account);
                                    }
                                  }}
                                  sx={{
                                    flex: "1 1 auto",
                                    minWidth: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    minHeight: 44,
                                    cursor: unavailable ? "not-allowed" : "pointer",
                                  }}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={selected}
                                    tabIndex={-1}
                                    disableRipple
                                    sx={{ p: 0, pointerEvents: "none", color: AH_BORDER, "&.Mui-checked": { color: AH_ACCENT } }}
                                  />
                                  <CloudProviderMark
                                    provider={selectedProvider}
                                    sx={{
                                      mr: 0,
                                      "& .cloud-provider-mark--aws": { height: 14, width: 26 },
                                      "& .cloud-provider-mark--azure": { height: 16, width: 16 },
                                    }}
                                  />
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: selected ? 700 : 500, color: AH_TEXT }}>
                                      {account.accountName || account.accountCode}
                                    </Typography>
                                    <Typography noWrap sx={{ fontSize: 12, color: AH_MUTED }}>
                                      {account.accountNumber}
                                    </Typography>
                                  </Box>
                                </Box>
                                {entry ? (
                                  <Button
                                    size="small"
                                    aria-expanded={expanded}
                                    aria-controls={panelId}
                                    aria-label={`Regions for ${accountLabel}: ${regionsSummary(entry.regions)}`}
                                    onClick={() => setExpandedAccount(expanded ? null : account.accountNumber)}
                                    endIcon={
                                      <ExpandMoreIcon
                                        sx={{ transition: "transform 0.2s ease", transform: expanded ? "rotate(180deg)" : "none" }}
                                      />
                                    }
                                    sx={{
                                      flexShrink: 0,
                                      minWidth: 0,
                                      px: 1,
                                      fontSize: 12,
                                      fontWeight: 600,
                                      textTransform: "none",
                                      color: AH_MUTED,
                                      whiteSpace: "nowrap",
                                      "& .MuiButton-endIcon": { ml: 0.5 },
                                      "&:hover": { backgroundColor: "rgba(249, 129, 37, 0.08)", color: AH_TEXT },
                                    }}
                                  >
                                    {regionsSummary(entry.regions)}
                                  </Button>
                                ) : null}
                              </Box>
                              {entry ? (
                                <Collapse
                                  in={expanded}
                                  timeout="auto"
                                  unmountOnExit
                                  onEntered={(node) =>
                                    node.parentElement?.scrollIntoView({ block: "nearest", behavior: "smooth" })
                                  }
                                >
                                  <Box
                                    id={panelId}
                                    role="group"
                                    aria-label={`Regions for ${accountLabel}`}
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 1,
                                      p: 1.5,
                                      borderTop: `1px solid ${AH_BORDER}`,
                                      backgroundColor: AH_WHITE,
                                    }}
                                  >
                                    <Chip
                                      label="All Regions"
                                      clickable
                                      aria-pressed={!entry.regions.length}
                                      onClick={() => toggleAccountRegion(account.accountNumber, null)}
                                      sx={regionChipSx(!entry.regions.length)}
                                    />
                                    {AWS_REGION_CODES.map((code) => {
                                      const regionSelected = entry.regions.includes(code);
                                      return (
                                        <Chip
                                          key={code}
                                          label={code}
                                          clickable
                                          aria-pressed={regionSelected}
                                          disabled={!regionSelected && entry.regions.length >= MAX_ACCOUNT_REGIONS}
                                          onClick={() => toggleAccountRegion(account.accountNumber, code)}
                                          sx={regionChipSx(regionSelected)}
                                        />
                                      );
                                    })}
                                  </Box>
                                </Collapse>
                              ) : null}
                            </Box>
                          );
                        })
                      )}
                    </Box>
                    {errors.accounts ? (
                      <Typography sx={{ fontSize: 12, color: "#dc2626", mt: 1.25 }}>{errors.accounts}</Typography>
                    ) : null}
                  </SectionCard>
                </Box>
            </Box>

            <Box
              sx={{
                ...tableShellSx,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                position: { md: "sticky" },
                bottom: 0,
                zIndex: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={() => history.push("/audit-hub")}
                sx={{
                  ...outlinedButtonSx,
                  backgroundColor: "#74788d",
                  color: AH_WHITE,
                  borderColor: "#74788d",
                  "&:hover": { backgroundColor: "#686c7f", borderColor: "#686c7f", boxShadow: "none" },
                }}
              >
                Cancel
              </Button>
              <Typography
                role={formError ? "alert" : "status"}
                sx={{
                  display: { xs: footerMessage ? "block" : "none", sm: "block" },
                  flex: { xs: "1 1 100%", sm: "1 1 0" },
                  order: { xs: 3, sm: 0 },
                  minWidth: 0,
                  fontSize: 12,
                  fontWeight: formError ? 600 : 400,
                  color: formError ? "#dc2626" : AH_MUTED,
                  textAlign: "center",
                  overflowWrap: "anywhere",
                }}
              >
                {footerMessage}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<SaveOutlinedIcon />}
                  onClick={handleSaveDraft}
                  disabled={!ready || savingDraft || launching}
                  sx={{
                    ...outlinedButtonSx,
                    backgroundColor: AH_WHITE,
                    color: AH_ACCENT,
                    borderColor: AH_ACCENT,
                    "&:hover": {
                      backgroundColor: AH_WHITE,
                      color: AH_ACCENT_HOVER,
                      borderColor: AH_ACCENT_HOVER,
                      boxShadow: "none",
                    },
                  }}
                >
                  {savingDraft ? "Saving..." : "Save Draft"}
                </Button>
                <Button
                  startIcon={<RocketLaunchOutlinedIcon />}
                  onClick={handleLaunch}
                  disabled={!ready || launching || savingDraft}
                  sx={primaryButtonSx}
                >
                  {launching ? "Launching..." : "Launch Assessment"}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </div>
      <LaunchAuditDialog
        open={confirmLaunch}
        audit={{ name: form.name.trim() }}
        onConfirm={submitLaunch}
        busy={launching}
        errorText={createState.status === "failed" ? createState.error || "" : ""}
        onClose={() => setConfirmLaunch(false)}
      />
    </AuditHubThemeProvider>
  );
};

export default CreateAudit;
